import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Activity, ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../i18n";

// Premium home-page market pulse: 6 live data cards with mini sparkline.
export default function MarketPulse() {
  const { lang } = useI18n();
  const pt = lang === "pt";
  const [data, setData] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/markets/pulse");
        if (alive) {
          setData(r.data.pulse || []);
          setUpdatedAt(r.data.updated_at);
        }
      } catch (e) {
        // silent fail; component just hides
      }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!data || data.length === 0) return null;

  const fmtTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleTimeString(pt ? "pt-BR" : "en-US", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <section className="relative max-w-[1400px] mx-auto px-6 lg:px-10 -mt-8 mb-12" data-testid="market-pulse">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold-400 font-mono">
          <Activity size={11} className="animate-pulse" />
          {pt ? "Pulso global" : "Global pulse"}
          <span className="ml-2 inline-flex items-center gap-1.5 text-[10px] text-neutral-500 normal-case tracking-normal">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            {pt ? "ao vivo" : "live"} · {fmtTime(updatedAt)}
          </span>
        </div>
        <Link to="/mercado" className="text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-gold-300 inline-flex items-center gap-1" data-testid="pulse-see-all">
          {pt ? "ver tudo" : "see all"} <ArrowUpRight size={11} />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {data.map((p) => (
          <PulseCard key={p.symbol} p={p} pt={pt} />
        ))}
      </div>

      <p className="mt-3 text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-600">
        {pt
          ? "Yahoo finance · coingecko · ECB · cache 5min · variação dos últimos 7 dias"
          : "Yahoo finance · coingecko · ECB · 5min cache · last 7 days change"}
      </p>
    </section>
  );
}

function PulseCard({ p, pt }) {
  const positive = (p.change_7d_pct || 0) >= 0;
  const TrendIcon = positive ? TrendingUp : TrendingDown;
  const trendColor = positive ? "text-emerald-300" : "text-red-300";
  const sparkColor = positive ? "#34d399" : "#f87171";

  const fmtValue = (v) => {
    if (v == null) return "—";
    if (p.kind === "fx") return v.toFixed(4);
    if (p.kind === "crypto") return `$${Math.round(v).toLocaleString()}`;
    return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  };

  return (
    <Link
      to={p.link || "/mercado"}
      data-testid={`pulse-card-${p.symbol}`}
      className="group surface p-4 hover:border-gold-500/30 transition-colors flex flex-col justify-between min-h-[120px] relative overflow-hidden"
    >
      <Sparkline points={p.spark || []} color={sparkColor} />
      <div className="relative z-10 flex items-start justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500">{p.symbol}</div>
          <div className="text-xs text-neutral-300 mt-0.5">{p.name}</div>
        </div>
        <TrendIcon size={14} className={trendColor} />
      </div>
      <div className="relative z-10 mt-3">
        <div className="font-display text-xl tracking-tight tabular-nums">{fmtValue(p.value)}</div>
        <div className={`text-xs font-mono mt-0.5 tabular-nums ${trendColor}`}>
          {positive ? "+" : ""}{(p.change_7d_pct || 0).toFixed(2)}% <span className="text-neutral-600">{pt ? "7d" : "7d"}</span>
        </div>
      </div>
    </Link>
  );
}

// Tiny inline SVG sparkline rendered behind the value.
function Sparkline({ points, color }) {
  if (!points || points.length < 2) return null;
  const W = 200;
  const H = 60;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const step = W / (points.length - 1);
  const path = points
    .map((v, i) => {
      const x = i * step;
      const y = H - ((v - min) / range) * H;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  const areaPath = `${path} L ${W} ${H} L 0 ${H} Z`;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="absolute inset-0 w-full h-full opacity-40 group-hover:opacity-60 transition-opacity pointer-events-none"
      aria-hidden
    >
      <defs>
        <linearGradient id={`grad-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#grad-${color.replace("#", "")})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
